// Shared derivations for the Tasks hub.
//
// Everything here works off the serialized occurrence payloads: each occurrence
// carries lifecycle_status ("new" | "acknowledged" | "in_progress" |
// "waiting_validation" | "completed"), the flags is_overdue / is_late / paused /
// is_pinned, and due_at / available_at. Eligibility comes from the store
// roster, so there is no coverage or schedule concept anywhere in here.

export const HATCH_STYLE = {
  backgroundImage:
    "repeating-linear-gradient(135deg, transparent 0 4px, rgba(100,116,139,.35) 4px 6px)",
};

// qhnum = hours as a float (7.5 = 7:30 AM). Values past 24 are overnight
// spill into the next day; display them as their clock time.
export const formatQhnum = (q) => {
  if (q === null || q === undefined) return "";
  const norm = q >= 24 ? q - 24 : q;
  const h = Math.floor(norm);
  const m = Math.round((norm - h) * 60);
  const ampm = h >= 12 ? "PM" : "AM";
  let hh = h % 12;
  if (hh === 0) hh = 12;
  return `${hh}${m ? ":" + String(m).padStart(2, "0") : ""} ${ampm}`;
};

export const windowLabel = (task) => {
  const lo = task.lower_bound_qhnum;
  const hi = task.upper_bound_qhnum;
  if (lo == null && hi == null) return "Anytime";
  // Legacy rows use a 0-0 window to mean "no window".
  if (lo === 0 && hi === 0) return "Anytime";
  if (lo != null && hi != null) {
    if (task.is_intraday && task.intraday_repeat_interval_minutes) {
      const iv = task.intraday_repeat_interval_minutes;
      const ivLabel = iv % 60 === 0 ? `${iv / 60} hr` : `${iv} min`;
      return `${formatQhnum(lo)}-${formatQhnum(hi)} every ${ivLabel}`;
    }
    return `${formatQhnum(lo)}-${formatQhnum(hi)}`;
  }
  if (lo != null) return `From ${formatQhnum(lo)}`;
  return `By ${formatQhnum(hi)}`;
};

// A date string plus a qhnum (hours past that day's midnight, which may run
// past 24 for an overnight window) as a local Date.
export const qhnumToDate = (dateStr, q) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(new Date(y, m - 1, d).getTime() + q * 3600 * 1000);
};

// ---------------------------------------------------------------------------
// Occurrence state
// ---------------------------------------------------------------------------

// One occurrence -> one visual state.
// "done" | "donelate" | "bad" | "waiting" | "warn" | "ack" | "idle"
// Overdue is a flag, so it outranks the in-flight states: an occurrence that
// is in progress but past due reads as overdue.
export const occState = (occ) => {
  const ls = occ.lifecycle_status;
  // The lifecycle status is authoritative and is checked first: the legacy
  // status has no word for work waiting on a manager, so it calls that work
  // "in_progress".
  if (ls === "waiting_validation") return "waiting";
  if (ls === "completed" || (!ls && (occ.status === "completed" || occ.status === "completed_late"))) {
    return occ.is_late || occ.status === "completed_late" ? "donelate" : "done";
  }
  if (occ.is_overdue || occ.status === "overdue") return "bad";
  if (ls === "in_progress" || occ.status === "in_progress") return "warn";
  if (ls === "acknowledged") return "ack";
  return "idle";
};

const memberLabel = (m) => {
  const stm = m?.store_team_member;
  return (
    stm?.full_name ||
    m?.full_name ||
    `${stm?.first_name || ""} ${stm?.last_name || ""}`
  ).trim();
};

// Aggregate one task's occurrences for one day.
export const rollupTask = (task) => {
  const occs = [...(task.plan_task_assignments || [])].sort(
    (a, b) => (a.intraday_slot_index ?? 0) - (b.intraday_slot_index ?? 0)
  );
  const states = occs.map((o) => occState(o));
  const count = (k) => states.filter((s) => s === k).length;

  const done = count("done") + count("donelate");
  const primaryOcc = occs[0] || null;
  const subs = (primaryOcc?.plan_subtask_assignments || []).filter(
    (s) => s.plan_subtask?.type !== "heading"
  );
  const subsDone = subs.filter((s) => s.is_completed || s.completed_at).length;

  let primary;
  if (states.length && states.every((s) => s === "done" || s === "donelate")) {
    primary = states.includes("donelate") ? "donelate" : "done";
  } else {
    primary =
      ["bad", "waiting", "warn", "ack", "idle"].find((k) => states.includes(k)) ||
      "idle";
  }

  const pinned = occs.some((o) => o.is_pinned);
  const pinnedNames = [];
  const pinnedMembers = [];
  const seenStmIds = new Set();
  occs.forEach((o) =>
    (o.plan_task_assignment_members || []).forEach((m) => {
      const label = memberLabel(m);
      if (label && !pinnedNames.includes(label)) pinnedNames.push(label);
      const stmId = m.store_team_member?.id ?? m.store_team_member_id;
      if (stmId && !seenStmIds.has(stmId)) {
        seenStmIds.add(stmId);
        pinnedMembers.push(m);
      }
    })
  );

  return {
    occs,
    states,
    total: occs.length,
    done,
    missed: count("bad"),
    waiting: count("waiting"),
    inProgress: count("warn"),
    acknowledged: count("ack"),
    notStarted: count("idle"),
    paused: occs.some((o) => o.paused),
    pinned,
    pinnedNames,
    pinnedMembers,
    subsDone,
    subsTotal: subs.length,
    primary,
    primaryOcc,
  };
};

export const statusLabelFor = (task, r) => {
  if (task.is_intraday) {
    const bits = [];
    if (r.done) bits.push(`${r.done} done`);
    if (r.missed) bits.push(`${r.missed} missed`);
    if (r.waiting) bits.push(`${r.waiting} waiting`);
    if (bits.length) return bits.join(" · ");
    return "Not started";
  }
  switch (r.primary) {
    case "done":
      return "Done";
    case "donelate":
      return "Done late";
    case "bad":
      return "Overdue";
    case "waiting":
      return "Waiting: Validation";
    case "warn":
      return "In progress";
    case "ack":
      return "Acknowledged";
    default:
      return "Not started";
  }
};

// Tailwind class fragments per state, shared by rows / dots / chips.
export const STATE_STYLES = {
  done: { rail: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
  donelate: { rail: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
  warn: { rail: "bg-violet-500", chip: "bg-violet-50 text-violet-700", dot: "bg-violet-500" },
  bad: { rail: "bg-rose-500", chip: "bg-rose-50 text-rose-700", dot: "bg-rose-500" },
  waiting: { rail: "bg-amber-500", chip: "bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  ack: { rail: "bg-blue-500", chip: "bg-blue-50 text-blue-700", dot: "bg-blue-500" },
  idle: { rail: "bg-slate-200", chip: "bg-slate-100 text-slate-600", dot: "border-2 border-slate-400" },
};

// Status pill grammar borrowed from the ticket UI (MaintenanceBadges):
// slate = untouched, blue = acknowledged, violet = we are on it,
// amber = blocked on someone, emerald = done.
export const OCC_STATUS_PILL = {
  new: { dot: "bg-slate-500", bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", label: "Not started" },
  acknowledged: { dot: "bg-blue-600", bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", label: "Acknowledged" },
  in_progress: { dot: "bg-violet-700", bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", label: "In Progress" },
  waiting_validation: { dot: "bg-amber-600", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", label: "Waiting: Validation" },
  completed: { dot: "bg-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", label: "Completed" },
  paused: { dot: "bg-slate-500", bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", label: "Paused" },
  // Derived, not stored: a past day's unsubmitted work that can no longer be
  // done. Same red as the grid's missed glyph.
  missed: { dot: "bg-red-600", bg: "bg-red-50", border: "border-red-200", text: "text-red-800", label: "Missed" },
};

// Overview grid glyphs: one circle per (task, period).
export const GLYPH_STATES = {
  done: { border: "border-emerald-600", bg: "bg-emerald-50", text: "text-emerald-600", label: "Done on time" },
  late: { border: "border-amber-600", bg: "bg-amber-50", text: "text-amber-700", label: "Done late" },
  missed: { border: "border-rose-600", bg: "bg-rose-50", text: "text-rose-600", label: "Missed" },
  waiting: { border: "border-amber-600", bg: "bg-amber-50", text: "text-amber-700", label: "Awaiting validation" },
  progress: { border: "border-violet-700", bg: "bg-violet-50", text: "text-violet-700", label: "In progress" },
  open: { border: "border-slate-400", bg: "bg-white", text: "text-slate-400", label: "Open" },
};

// ---------------------------------------------------------------------------
// Cadence
// ---------------------------------------------------------------------------

export const CADENCE_OPTIONS = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "one_off", label: "One-off" },
];

const DOW_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // Python weekday, Mon=0

export const ordinal = (n) => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

// The cadence key, derived for rows that predate the column.
export const cadenceKey = (task) => {
  if (task?.cadence) return task.cadence;
  if (task?.type === "one_off") return "one_off";
  const days = task?.days_of_week || [];
  if (days.length === 7) return "daily";
  if (days.length > 0) return "weekly";
  return task?.repeating_period === "monthly" ? "monthly" : "daily";
};

const dayRangeLabel = (days) => {
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.length === 7) return "";
  if (sorted.length === 5 && sorted.join() === "0,1,2,3,4") return "Mon to Fri";
  if (sorted.length === 2 && sorted.join() === "5,6") return "Sat and Sun";
  return sorted.map((d) => DOW_SHORT[d]).join(", ");
};

export const formatDateShort = (value) => {
  if (!value) return "";
  const d = typeof value === "string" && value.length === 10 ? new Date(`${value}T12:00:00`) : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

export const cadenceLabel = (task) => {
  const key = cadenceKey(task);
  const days = task?.days_of_week || [];
  switch (key) {
    case "daily": {
      const range = dayRangeLabel(days);
      return range ? `Daily · ${range}` : "Daily";
    }
    case "weekly":
      return "Weekly · Any day";
    case "monthly":
      return "Monthly · Any day";
    case "one_off": {
      const when = task?.due_at || task?.one_off_date || task?.in_effect_lower_bound_date;
      const label = formatDateShort(when);
      return label ? `One-off · ${label}` : "One-off";
    }
    default:
      return "Not scheduled";
  }
};

export const taskSubline = (pt) => {
  const steps = pt?.subtask_count ?? pt?.required_step_count ?? (pt?.plan_subtasks || []).filter((s) => s.type !== "heading").length;
  const origin = pt?.created_by_label || (cadenceKey(pt) === "one_off" ? "From HQ" : "Routine");
  return `${origin} · ${steps} step${steps === 1 ? "" : "s"}`;
};

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------

// Every time on these surfaces is read in the STORE's zone, which the payloads
// carry: a manager in Eastern time must read a 5 PM Pacific deadline as 5 PM,
// on the store's own calendar day. Falls back to the device zone when a payload
// predates the field.
export const timeOnly = (d, tz) =>
  d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: tz || undefined });

// The store's calendar day for an instant, as YYYY-MM-DD, so "today" and
// "same day" are judged where the work happens.
export const dayInZone = (d, tz) =>
  tz
    ? d.toLocaleDateString("en-CA", { timeZone: tz })
    : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const formatDateTimeShort = (d, now = new Date(), tz) => {
  const sameDay = dayInZone(d, tz) === dayInZone(now, tz);
  if (sameDay) return `Today, ${timeOnly(d, tz)}`;
  const diffDays = Math.round((d - now) / 86400000);
  if (diffDays > 0 && diffDays < 7) {
    return `${d.toLocaleDateString(undefined, { weekday: "short", timeZone: tz || undefined })}, ${timeOnly(d, tz)}`;
  }
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: tz || undefined })}, ${timeOnly(d, tz)}`;
};

export const durationLabel = (ms) => {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

// Time on task for one occurrence. work_seconds is the server's sum at fetch
// time; a running occurrence keeps counting locally from the moment the
// payload arrived so the label ticks without a refetch.
// The server's idle sweep closes a session with no activity for this long
// (plan_info work_sessions.IDLE_MINUTES), so a screen left open without a
// refetch never extrapolates past the point the clock could still be running.
export const MAX_LOCAL_EXTRAPOLATION_MS = 60 * 60 * 1000;

// work_seconds is the SUM of every session, so wall time since the fetch is
// added once per open session (two people working = two seconds per second).
// A payload without the count (older shape) counts one while running.
const clampDelta = (untilMs, fetchedAtMs) =>
  Math.min(Math.max(0, untilMs - fetchedAtMs), MAX_LOCAL_EXTRAPOLATION_MS);

// True once any open session's idle expiry has passed since the payload
// arrived: the sweep (every few minutes) will trim it unless a save touches
// it first, so the local clock is a best guess until the next fetch. Callers
// mark the value "~" and refetch where they can.
export const workClockIsProvisional = (row, nowMs) => {
  const expiries = Array.isArray(row?.work_open_sessions)
    ? row.work_open_sessions.map((s) => Date.parse(s?.expires_at))
    : Array.isArray(row?.work_open_expiries)
    ? row.work_open_expiries.map((e) => Date.parse(e))
    : [];
  return expiries.some((t) => !Number.isNaN(t) && nowMs >= t);
};

export const workElapsedMs = (row, fetchedAtMs, nowMs) => {
  const base = (row?.work_seconds || 0) * 1000;
  // The server describes each open session (work_open_sessions): when the
  // sweep may close it, and how many of the summed seconds it would take
  // back (the stretch since last activity). Before the expiry the session's
  // share advances; once it passes, that stretch is rolled back, which is
  // exactly the trimmed value the next fetch will report.
  const sessions = Array.isArray(row?.work_open_sessions)
    ? row.work_open_sessions
        .map((s) => ({ expiresAt: Date.parse(s?.expires_at), trimMs: (s?.trim_seconds || 0) * 1000 }))
        .filter((s) => !Number.isNaN(s.expiresAt))
    : [];
  if (sessions.length) {
    const total = sessions.reduce(
      (sum, s) => sum + (nowMs < s.expiresAt ? clampDelta(nowMs, fetchedAtMs) : -s.trimMs),
      base
    );
    return Math.max(0, total);
  }
  // Older payload shapes: expiries only, or a count and at most one expiry.
  const expiries = Array.isArray(row?.work_open_expiries)
    ? row.work_open_expiries.map((e) => Date.parse(e)).filter((t) => !Number.isNaN(t))
    : [];
  if (expiries.length) {
    return base + expiries.reduce((sum, t) => sum + clampDelta(Math.min(nowMs, t), fetchedAtMs), 0);
  }
  // Older payload shapes: one count and at most one expiry.
  const runningCount = row?.work_running_count ?? (row?.work_running ? 1 : 0);
  const expiresAt = row?.work_idle_expires_at ? Date.parse(row.work_idle_expires_at) : NaN;
  const until = Number.isNaN(expiresAt) ? nowMs : Math.min(nowMs, expiresAt);
  return base + clampDelta(until, fetchedAtMs) * runningCount;
};

// One clock per person with an open session, for the chips: their recorded
// seconds (assignees[].work_seconds on a Now row) plus wall time since the
// fetch, each advancing to its own idle expiry and rolled back by its own
// trim once that passes, the way the summed clock treats every session.
// Only when two or more people are mid-session: one person's clock IS the
// total, and the summed chip reads better then. Empty, so the caller keeps
// the summed chip, whenever the per-person view cannot be trusted: an
// occurrence opened from the day payload carries no assignees, an older
// payload does not tag its sessions with a member, and time recorded by
// someone since taken off the occurrence is on nobody's row.
export const memberClocks = (row, fetchedAtMs, nowMs) => {
  const ids = row?.work_open_member_ids || [];
  if (ids.length < 2) return [];
  const names = {};
  const seconds = {};
  (row.plan_task_assignment_members || []).forEach((m) => {
    const stm = m?.store_team_member;
    if (stm?.id != null && stm.full_name) names[stm.id] = stm.full_name;
  });
  (row.assignees || []).forEach((a) => {
    if (a?.store_team_member_id == null) return;
    if (a.full_name) names[a.store_team_member_id] = a.full_name;
    seconds[a.store_team_member_id] = a.work_seconds || 0;
  });
  const sessionByMember = {};
  (Array.isArray(row.work_open_sessions) ? row.work_open_sessions : []).forEach((s) => {
    const expiresAt = Date.parse(s?.expires_at);
    if (s?.store_team_member_id != null && !Number.isNaN(expiresAt)) {
      sessionByMember[s.store_team_member_id] = { expiresAt, trimMs: (s.trim_seconds || 0) * 1000 };
    }
  });
  if (ids.some((id) => seconds[id] === undefined || !sessionByMember[id])) return [];
  // The per-person totals must account for the whole summed clock. Time
  // recorded by someone no longer on the occurrence (unpinned, or a deleted
  // team member) is in work_seconds but on nobody's row, and chips that left
  // it out would read less than the clock they replace.
  if (row.work_seconds == null) return [];
  const accounted = Object.values(seconds).reduce((sum, secs) => sum + secs, 0);
  if (accounted !== (row.work_seconds || 0)) return [];
  const open = ids.map((id) => {
    const session = sessionByMember[id];
    const advance = nowMs < session.expiresAt ? clampDelta(nowMs, fetchedAtMs) : -session.trimMs;
    return {
      id,
      name: names[id] || "Someone",
      ms: Math.max(0, seconds[id] * 1000 + advance),
      running: true,
    };
  });
  // Whoever worked on it earlier and stopped (paused, trimmed, or done for
  // now) keeps a still chip, so the chips together never read less than the
  // summed clock they replace.
  const closed = Object.keys(seconds)
    .map(Number)
    .filter((id) => !ids.includes(id) && seconds[id] > 0)
    .map((id) => ({ id, name: names[id] || "Someone", ms: seconds[id] * 1000, running: false }));
  return [...open, ...closed];
};

// Ticking clock: m:ss under an hour, h:mm:ss above.
export const elapsedClock = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${ss}`;
  return `${m}:${ss}`;
};

// The people with an open session on this occurrence other than the actor,
// by name. The actor's own clock is my_session_running; while it is not
// running every open session is somebody else's, which is the only time the
// modal needs names: the chip and the Start prompt say that Start joins a
// running task rather than taking it over. The fresh Now row's assignees are
// read first (a coworker who started after the day payload loaded is only
// there), then the occurrence's members.
export const othersWorkingNames = (occ) => {
  if (!occ || occ.my_session_running) return [];
  const ids = occ.work_open_member_ids || [];
  if (!ids.length) return [];
  const byId = {};
  (occ.plan_task_assignment_members || []).forEach((m) => {
    const stm = m?.store_team_member;
    if (stm?.id != null && stm.full_name) byId[stm.id] = stm.full_name;
  });
  (occ.assignees || []).forEach((a) => {
    if (a?.store_team_member_id != null && a.full_name) byId[a.store_team_member_id] = a.full_name;
  });
  return ids.map((id) => byId[id]).filter(Boolean);
};

// "Ana", "Ana and Ben", "Ana and 2 others".
export const namesPhrase = (names) => {
  if (!names?.length) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]} and ${names.length - 1} others`;
};

// The line above Start while somebody else's clock runs.
export const joinPrompt = (names) =>
  `${namesPhrase(names)} ${names.length === 1 ? "is" : "are"} working on this. Start to join in and track your own time.`;

// Due column: {primary, secondary, overdue}.
export const dueLabel = (occ, now = new Date(), tz) => {
  if (!occ?.due_at) return { primary: "Anytime", secondary: null, overdue: false };
  const due = new Date(occ.due_at);
  if (Number.isNaN(due.getTime())) return { primary: "Anytime", secondary: null, overdue: false };
  const overdue = !!occ.is_overdue && !occ.submitted_at;
  if (overdue) {
    return {
      primary: `Overdue ${durationLabel(now - due)}`,
      secondary: `Was due ${
        dayInZone(due, tz) === dayInZone(now, tz) ? timeOnly(due, tz) : formatDateTimeShort(due, now, tz)
      }`,
      overdue: true,
    };
  }
  return { primary: formatDateTimeShort(due, now, tz), secondary: null, overdue: false };
};

// ---------------------------------------------------------------------------
// Period windows for the Overview stepper
// ---------------------------------------------------------------------------

export const formatDateYMD = (date) => {
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return [d.getFullYear(), month, day].join("-");
};

export const addDays = (theDate, days) => {
  const newDate = new Date(theDate);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

export const addMonths = (theDate, months) => {
  const d = new Date(theDate);
  return new Date(d.getFullYear(), d.getMonth() + months, 1);
};

// JS getDay() index (Sun=0) of the store's business week start.
export const getBusinessWeekStartDayIndex = (storeNames, store_id) => {
  const day_of_week_names = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  try {
    const idx = day_of_week_names.indexOf(
      storeNames.filter((v) => v.id === store_id)[0]["business_week_start_day"]
    );
    return idx === -1 ? 1 : idx;
  } catch (error) {
    return 1;
  }
};

export const startOfBusinessWeek = (date, weekStartIndex) => {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  const diff = (d.getDay() - weekStartIndex + 7) % 7;
  return addDays(d, -diff);
};

// The window the Overview shows for a cadence around an anchor date.
export const periodWindow = (cadence, anchor, weekStartIndex = 1) => {
  const base = new Date(anchor);
  base.setHours(12, 0, 0, 0);
  switch (cadence) {
    case "weekly": {
      const start = addDays(startOfBusinessWeek(base, weekStartIndex), -28);
      return { start, end: addDays(start, 34) };
    }
    case "monthly": {
      const start = addMonths(base, -5);
      const end = addDays(addMonths(base, 1), -1);
      return { start, end };
    }
    case "one_off": {
      const start = new Date(base.getFullYear(), base.getMonth(), 1);
      return { start, end: addDays(addMonths(base, 1), -1) };
    }
    default: {
      // Daily shows the trailing week ending today, so the grid is history
      // rather than empty future columns; weekly and monthly likewise end on
      // the current period.
      const start = addDays(base, -6);
      return { start, end: base };
    }
  }
};

// Step the window one period back or forward.
export const stepWindow = (cadence, start, dir) => {
  switch (cadence) {
    case "weekly":
      return addDays(start, 7 * dir);
    case "monthly":
    case "one_off":
      return addMonths(start, dir);
    default:
      return addDays(start, 7 * dir);
  }
};

const monthShort = (d) => d.toLocaleDateString(undefined, { month: "short" });
const monthDay = (d) => `${monthShort(d)} ${d.getDate()}`;

// Same wording the backend returns in window.label, computed locally so the
// stepper never waits on a response.
export const windowLabelFor = (cadence, start) => {
  const s = new Date(start);
  s.setHours(12, 0, 0, 0);
  switch (cadence) {
    case "monthly": {
      const last = addMonths(s, 5);
      return `${monthShort(s)} ${s.getFullYear()} to ${monthShort(last)} ${last.getFullYear()}`;
    }
    case "one_off":
      return s.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    case "weekly":
      return `${monthDay(s)} to ${monthDay(addDays(s, 34))}`;
    default:
      return `${monthDay(s)} to ${monthDay(addDays(s, 6))}`;
  }
};

export const resetLabelFor = (cadence) =>
  cadence === "monthly" || cadence === "one_off" ? "This month" : cadence === "weekly" ? "This week" : "Last 7 days";

// Where a task runs, for the Library: "All locations", the picked regions and
// districts by name, plus stores picked on their own as a count.
export const scopeLabel = (task, groups) => {
  if (!task?.is_location_specific) return "All locations";
  const picked = task.location_specific_store_groups || [];
  const names = picked.map((uuid) => groups?.find((g) => g.uuid === uuid)?.name).filter(Boolean);
  const expanded = task.location_specific_stores || [];
  // Without the group names (payload not loaded, or an older backend) the
  // expanded store list is the honest answer.
  if (picked.length && names.length < picked.length) {
    return expanded.length ? `${expanded.length} ${expanded.length === 1 ? "location" : "locations"}` : "No locations";
  }
  const explicit = task.location_specific_explicit_stores ?? expanded;
  const parts = [];
  if (names.length) parts.push(names.join(", "));
  if (explicit.length) parts.push(`${explicit.length} ${explicit.length === 1 ? "location" : "locations"}`);
  return parts.join(" + ") || "No locations";
};

// Permission-based audience picks and how they read; a task's audience can
// name these beside roles (a scored audit is conducted by exactly them).
export const AUDIENCE_PERMISSION_OPTIONS = [
  { key: "company_admin", label: "Company admins", hint: "HQ and district leads" },
  { key: "company_manager", label: "Store managers", hint: "Managers at each location" },
];

export const audienceLabel = (task, roleNames) => {
  const picks = task?.audience_permission_types || [];
  const permissionNames = AUDIENCE_PERMISSION_OPTIONS.filter((o) => picks.includes(o.key)).map((o) => o.label);
  const ids = task?.auto_assignable_roles || [];
  if (!ids.length && !permissionNames.length) return "Anyone";
  const names = ids
    .map((id) => roleNames?.find((r) => r.id === id)?.role_name)
    .filter(Boolean);
  const parts = [...permissionNames, ...names];
  return parts.length ? parts.join(" · ") : "Selected roles";
};

// Whether a roster member is in a task's audience by permission.
export const memberMatchesAudiencePermission = (member, task) => {
  const picks = task?.audience_permission_types || [];
  const pt = member?.permission_type;
  if (picks.includes("company_admin") && (pt === "company_admin" || pt === "rightwork_internal")) return true;
  if (picks.includes("company_manager") && pt === "company_manager") return true;
  return false;
};

// Whether the day payload says the requesting manager may work this
// occurrence. A payload without permissions (an older backend) says yes.
export const canWorkOccurrence = (occ) => {
  const p = occ?.permissions;
  if (!p) return true;
  return !!(p.can_start || p.can_submit || p.can_resume || p.can_acknowledge);
};

// Whether the day payload says the requesting manager may pin this
// occurrence. Pinning is a manager's act whatever the audience (a manager
// outside a scored audit's audience still hands it to an eligible admin).
export const canPinOccurrence = (occ) => {
  const p = occ?.permissions;
  if (!p) return true;
  return p.can_pin !== false;
};



// A step's photos, oldest first. Older payloads carry only file_upload.
export const MAX_STEP_PHOTOS = 10;

export const stepPhotos = (s) => {
  if (Array.isArray(s?.file_uploads)) return s.file_uploads;
  return s?.file_upload ? [s.file_upload] : [];
};

// The step with its photos replaced. photo_uuids is what the save endpoints
// read; file_upload stays the first photo for anything that shows one.
export const withStepPhotos = (s, photos) => ({
  ...s,
  file_uploads: photos,
  file_upload: photos[0] || null,
  photo_uuids: photos.map((p) => p.uuid),
});

// Local completeness for one subtask answer, mirroring the backend rules: a
// photo-required item needs its photo; a checkbox needs its CURRENT checked
// value; a text item needs its CURRENT answer. is_completed is only a fallback
// for rows whose editable value fields are untouched -- once someone clears a
// box or an answer, the stale flag must not keep the item "complete".
export const locallyDone = (s) => {
  const sub = s.plan_subtask || {};
  const hasPhoto = stepPhotos(s).length > 0;
  if (sub.type === "pass_fail") {
    // An inspection item is answered either way: a Fail is a complete
    // answer, and a cleared verdict is not one (the backend never marks a
    // pass/fail item complete without a verdict, so the stale flag is never
    // consulted). A photo-required one needs the photo AND the verdict.
    const answered = s.value_boolean != null;
    return sub.is_image_required ? hasPhoto && answered : answered;
  }

  if (sub.is_image_required) return hasPhoto;
  if (sub.type === "checkbox") {
    return s.value_boolean == null ? !!s.is_completed : !!s.value_boolean;
  }
  if (sub.type === "text") {
    return s.value_text == null ? !!s.is_completed : !!String(s.value_text).trim();
  }
  return !!(
    s.value_boolean ||
    (s.value_text && String(s.value_text).trim()) ||
    s.is_completed
  );
};

// A required step still waiting for an answer: not optional, not a heading,
// and not locallyDone.
const isOpenRequiredStep = (s) =>
  !!s && !s.plan_subtask?.is_optional && s.plan_subtask?.type !== "heading" && !locallyDone(s);

// The next open required step at or after `fromIndex`, wrapping to the top,
// or -1 when every required step is answered.
export const nextOpenRequiredStep = (steps, fromIndex = 0) => {
  const list = steps || [];
  const start = Math.max(0, Math.min(fromIndex || 0, list.length));
  for (let offset = 0; offset < list.length; offset += 1) {
    const i = (start + offset) % list.length;
    if (isOpenRequiredStep(list[i])) return i;
  }
  return -1;
};

// Where a checklist picks up when it opens: the first open required step,
// but only once some required step is answered. A fresh checklist opens at
// the top, and a finished one has nowhere to go (-1 for both).
export const resumeStepIndex = (steps) => {
  const list = steps || [];
  const started = list.some(
    (s) => !s.plan_subtask?.is_optional && s.plan_subtask?.type !== "heading" && locallyDone(s)
  );
  return started ? nextOpenRequiredStep(list, 0) : -1;
};

// ---------------------------------------------------------------------------
// Scored audits
// ---------------------------------------------------------------------------

// The backend's defaults (services/scoring.py): an A at 90, a B at 80, and
// anything under is re-audited.
export const DEFAULT_GRADE_BANDS = [
  { label: "A", min_percent: 90 },
  { label: "B", min_percent: 80 },
  { label: "Re-audit", min_percent: 0 },
];

// Bands highest first, junk dropped, the way the backend reads them.
export const sortBands = (bands) =>
  (Array.isArray(bands) ? bands : [])
    .filter((b) => b && String(b.label || "").trim() && Number.isFinite(Number(b.min_percent)))
    .map((b) => ({ label: String(b.label).trim(), min_percent: Number(b.min_percent) }))
    .sort((a, b) => b.min_percent - a.min_percent);

// The first band (highest first) whose minimum the percent reaches.
export const gradeFor = (percent, bands) => {
  if (percent == null) return null;
  const sorted = sortBands(bands);
  const hit = sorted.find((b) => percent >= b.min_percent);
  return hit ? hit.label : sorted.length ? sorted[sorted.length - 1].label : null;
};

// Colour for a grade by its rank in the bands: the top band reads as good,
// the bottom as bad, anything between as a warning. Unknown grades are slate.
export const bandTone = (grade, bands) => {
  const sorted = sortBands(bands);
  const idx = sorted.findIndex((b) => b.label === grade);
  if (idx === -1 || grade == null) {
    return { chip: "bg-slate-100 text-slate-600 border-slate-200", text: "text-slate-600", bar: "bg-slate-400", hex: "#94a3b8" };
  }
  if (idx === 0) {
    return { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", text: "text-emerald-700", bar: "bg-emerald-600", hex: "#059669" };
  }
  if (idx === sorted.length - 1) {
    return { chip: "bg-rose-50 text-rose-700 border-rose-200", text: "text-rose-700", bar: "bg-rose-600", hex: "#e11d48" };
  }
  return { chip: "bg-amber-50 text-amber-800 border-amber-200", text: "text-amber-800", bar: "bg-amber-600", hex: "#d97706" };
};

// "187 / 238 · 79%" for a scored occurrence, or null before it is scored.
export const scoreLabel = (occ) => {
  if (!occ || occ.score_total == null) return null;
  const pct = occ.score_percent == null ? "–" : `${occ.score_percent}%`;
  return `${occ.score_passed ?? 0} / ${occ.score_total} · ${pct}`;
};

// Whether an answer row was one of the audit's items: by the submit-time
// stamp the payload carries (audit_item_uuids) when there is one, else by
// the item's current type. Mirrors the backend's is_audit_answer.
export const isAuditItem = (occ, s) => {
  const stamped = occ?.audit_item_uuids;
  if (Array.isArray(stamped)) return stamped.includes(s.plan_subtask?.uuid);
  return s.plan_subtask?.type === "pass_fail";
};

// The failed inspection items of an occurrence, in checklist order.
export const failedSteps = (occ) =>
  (occ?.plan_subtask_assignments || []).filter(
    (s) => isAuditItem(occ, s) && s.value_boolean === false
  );


// Lay a Now row's lifecycle and work fields over a day-payload occurrence.

// The day payload can lag a transition by one round trip and carries no
// per-person session; the Now row is authoritative for both, so the checklist
// modal's Start / Pause state and clock come from here.
export const mergeNowRowOverOccurrence = (row, occ) => ({
  ...occ,
  plan_task: occ.plan_task || row.plan_task,
  lifecycle_status: row.status || occ.lifecycle_status,
  is_overdue: row.is_overdue ?? occ.is_overdue,
  is_late: row.is_late ?? occ.is_late,
  paused: row.paused ?? occ.paused,
  started_at: row.started_at ?? occ.started_at,
  acknowledged_at: row.acknowledged_at ?? occ.acknowledged_at,
  work_seconds: row.work_seconds ?? occ.work_seconds,
  work_running: row.work_running ?? occ.work_running,
  work_running_count: row.work_running_count ?? occ.work_running_count,
  work_open_member_ids: row.work_open_member_ids ?? occ.work_open_member_ids,
  work_idle_expires_at: row.work_idle_expires_at ?? occ.work_idle_expires_at,
  work_open_expiries: row.work_open_expiries ?? occ.work_open_expiries,
  work_open_sessions: row.work_open_sessions ?? occ.work_open_sessions,
  work_estimated: row.work_estimated ?? occ.work_estimated,
  my_session_running: row.my_session_running ?? occ.my_session_running,
  assignees: row.assignees ?? occ.assignees,
  permissions: row.permissions ?? occ.permissions,
});


// The store-local hour (as a qhnum, 13.5 = 1:30 PM) written into an ISO
// timestamp the backend formatted in the store's timezone (available_at,
// due_at). Read off the string: parsing it with Date and asking for
// getHours() would answer in the BROWSER's timezone, which puts an 11 AM
// slot at 9 AM for a manager two zones away. Null when the string is not
// an ISO timestamp.
export function wallClockQhnum(iso) {
  const m = /T(\d\d):(\d\d)/.exec(iso || "");
  if (!m) return null;
  return Number(m[1]) + Number(m[2]) / 60;
}
